/**
 * RepConnect - Attendee Service
 *
 * Manages meeting attendees and RSVP tracking
 * Part of Phase 2: Multi-Physician Office Lunch Support
 */

import { supabase } from '../utils/supabase';
import { logger } from '../utils/logger';

/**
 * Meeting Attendee Service
 * Handles individual attendee management and RSVP status
 */
export const attendeeService = {
  /**
   * Get all attendees for a meeting
   * @param {string} meetingId - UUID of the meeting
   * @returns {Promise<Array>} Array of attendees with user details
   */
  getMeetingAttendees: async (meetingId) => {
    try {
      const { data, error } = await supabase
        .from('meeting_attendees')
        .select(`
          *,
          user_profiles:user_id (
            id,
            first_name,
            last_name,
            email,
            specialty,
            user_type,
            company,
            products,
            territory,
            title,
            npi_number,
            phone
          )
        `)
        .eq('meeting_id', meetingId)
        .order('attendee_type', { ascending: true })
        .order('is_organizer', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting meeting attendees:', error);
      throw error;
    }
  },

  /**
   * Get meetings a user is attending
   * @param {string} userId - UUID of the user
   * @returns {Promise<Array>} Array of meetings with attendee details
   */
  getUserMeetings: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('meeting_attendees')
        .select(`
          *,
          meetings (
            id,
            meeting_date,
            start_time,
            status,
            location_name,
            location_address,
            restaurant_name,
            sales_rep_id,
            customer_id
          )
        `)
        .eq('user_id', userId)
        .order('meetings(meeting_date)', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting user meetings:', error);
      throw error;
    }
  },

  /**
   * Add a single attendee to a meeting
   * @param {Object} attendeeData - Attendee information
   * @param {string} attendeeData.meeting_id - UUID of the meeting
   * @param {string} attendeeData.user_id - UUID of the user
   * @param {string} attendeeData.attendee_type - 'physician', 'staff', or 'sales_rep'
   * @param {boolean} attendeeData.is_organizer - Is this the meeting organizer?
   * @param {boolean} attendeeData.is_guest - Is this an external guest?
   * @param {string} attendeeData.rsvp_status - 'pending', 'accepted', 'declined', 'maybe'
   * @returns {Promise<Object>} Created attendee record
   */
  addAttendee: async (attendeeData) => {
    try {
      const { data, error } = await supabase
        .from('meeting_attendees')
        .insert([{
          ...attendeeData,
          rsvp_at: attendeeData.rsvp_status !== 'pending' ? new Date().toISOString() : null
        }])
        .select()
        .single();

      if (error) {
        // Handle unique constraint violation
        if (error.code === '23505') {
          throw new Error('User is already an attendee of this meeting');
        }
        throw error;
      }

      logger.info('Attendee added to meeting:', {
        meetingId: attendeeData.meeting_id,
        userId: attendeeData.user_id,
        type: attendeeData.attendee_type
      });

      return data;
    } catch (error) {
      logger.error('Error adding attendee:', error);
      throw error;
    }
  },

  /**
   * Add multiple attendees to a meeting at once
   * @param {string} meetingId - UUID of the meeting
   * @param {Array} attendees - Array of attendee objects
   * @returns {Promise<Array>} Created attendee records
   */
  addMultipleAttendees: async (meetingId, attendees) => {
    try {
      const attendeeRecords = attendees.map(attendee => ({
        meeting_id: meetingId,
        user_id: attendee.user_id,
        attendee_type: attendee.attendee_type || 'physician',
        is_organizer: attendee.is_organizer || false,
        is_guest: attendee.is_guest || false,
        rsvp_status: attendee.rsvp_status || 'pending',
        rsvp_at: attendee.rsvp_status && attendee.rsvp_status !== 'pending'
          ? new Date().toISOString()
          : null
      }));

      const { data, error } = await supabase
        .from('meeting_attendees')
        .insert(attendeeRecords)
        .select();

      if (error) throw error;

      logger.info('Multiple attendees added to meeting:', {
        meetingId,
        count: data.length
      });

      return data || [];
    } catch (error) {
      logger.error('Error adding multiple attendees:', error);
      throw error;
    }
  },

  /**
   * Update attendee RSVP status
   * @param {string} attendeeId - UUID of the meeting_attendees record
   * @param {string} rsvpStatus - 'accepted', 'declined', 'maybe', 'pending'
   * @param {string} declineReason - Optional reason for declining
   * @returns {Promise<Object>} Updated attendee record
   */
  updateRSVP: async (attendeeId, rsvpStatus, declineReason = null) => {
    try {
      const updates = {
        rsvp_status: rsvpStatus,
        rsvp_at: new Date().toISOString()
      };

      if (rsvpStatus === 'declined' && declineReason) {
        updates.decline_reason = declineReason;
      }

      const { data, error } = await supabase
        .from('meeting_attendees')
        .update(updates)
        .eq('id', attendeeId)
        .select()
        .single();

      if (error) throw error;

      logger.info('RSVP updated:', {
        attendeeId,
        status: rsvpStatus
      });

      return data;
    } catch (error) {
      logger.error('Error updating RSVP:', error);
      throw error;
    }
  },

  /**
   * Update attendee RSVP by user ID and meeting ID
   * Useful when you have user ID but not attendee record ID
   * @param {string} userId - UUID of the user
   * @param {string} meetingId - UUID of the meeting
   * @param {string} rsvpStatus - 'accepted', 'declined', 'maybe', 'pending'
   * @param {string} declineReason - Optional reason for declining
   * @returns {Promise<Object>} Updated attendee record
   */
  updateRSVPByUser: async (userId, meetingId, rsvpStatus, declineReason = null) => {
    try {
      // Use RPC function to bypass RLS (similar to get_meetings_for_attendee)
      const { data, error } = await supabase
        .rpc('update_attendee_rsvp', {
          user_id_param: userId,
          meeting_id_param: meetingId,
          rsvp_status_param: rsvpStatus,
          decline_reason_param: declineReason
        });

      if (error) {
        // Fallback to direct update if RPC doesn't exist
        const updates = {
          rsvp_status: rsvpStatus,
          rsvp_at: new Date().toISOString()
        };

        if (rsvpStatus === 'declined' && declineReason) {
          updates.decline_reason = declineReason;
        }

        const { data: directData, error: directError } = await supabase
          .from('meeting_attendees')
          .update(updates)
          .eq('user_id', userId)
          .eq('meeting_id', meetingId)
          .select();

        if (directError) throw directError;
        if (!directData || directData.length === 0) {
          throw new Error(`No attendee record found for user ${userId} in meeting ${meetingId}`);
        }
        return directData[0];
      }

      logger.info('RSVP updated by user:', {
        userId,
        meetingId,
        status: rsvpStatus
      });

      return data;
    } catch (error) {
      logger.error('Error updating RSVP by user:', error);
      throw error;
    }
  },

  /**
   * Mark attendee attendance (post-meeting)
   * @param {string} attendeeId - UUID of the meeting_attendees record
   * @param {boolean} attended - Did they attend?
   * @param {string} notes - Optional notes
   * @returns {Promise<Object>} Updated attendee record
   */
  markAttendance: async (attendeeId, attended, notes = null) => {
    try {
      const updates = { attended };
      if (notes) {
        updates.notes = notes;
      }

      const { data, error } = await supabase
        .from('meeting_attendees')
        .update(updates)
        .eq('id', attendeeId)
        .select()
        .single();

      if (error) throw error;

      logger.info('Attendance marked:', {
        attendeeId,
        attended
      });

      return data;
    } catch (error) {
      logger.error('Error marking attendance:', error);
      throw error;
    }
  },

  /**
   * Remove an attendee from a meeting
   * @param {string} attendeeId - UUID of the meeting_attendees record
   * @returns {Promise<void>}
   */
  removeAttendee: async (attendeeId) => {
    try {
      const { error } = await supabase
        .from('meeting_attendees')
        .delete()
        .eq('id', attendeeId);

      if (error) throw error;

      logger.info('Attendee removed from meeting:', { attendeeId });
    } catch (error) {
      logger.error('Error removing attendee:', error);
      throw error;
    }
  },

  /**
   * Get attendee count by RSVP status for a meeting
   * @param {string} meetingId - UUID of the meeting
   * @returns {Promise<Object>} Object with counts by status
   */
  getAttendeeCounts: async (meetingId) => {
    try {
      const { data, error } = await supabase
        .from('meeting_attendees')
        .select('rsvp_status, attendee_type')
        .eq('meeting_id', meetingId);

      if (error) throw error;

      const counts = {
        total: data.length,
        accepted: 0,
        declined: 0,
        pending: 0,
        maybe: 0,
        physicians: 0,
        staff: 0,
        sales_reps: 0
      };

      data.forEach(attendee => {
        counts[attendee.rsvp_status]++;

        if (attendee.attendee_type === 'physician') counts.physicians++;
        else if (attendee.attendee_type === 'staff') counts.staff++;
        else if (attendee.attendee_type === 'sales_rep') counts.sales_reps++;
      });

      return counts;
    } catch (error) {
      logger.error('Error getting attendee counts:', error);
      throw error;
    }
  },

  /**
   * Get meeting organizer (the physician who was originally searched for)
   * @param {string} meetingId - UUID of the meeting
   * @returns {Promise<Object|null>} Organizer attendee record or null
   */
  getMeetingOrganizer: async (meetingId) => {
    try {
      const { data, error } = await supabase
        .from('meeting_attendees')
        .select(`
          *,
          user_profiles:user_id (
            id,
            first_name,
            last_name,
            email,
            specialty,
            user_type
          )
        `)
        .eq('meeting_id', meetingId)
        .eq('is_organizer', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No organizer found
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error getting meeting organizer:', error);
      throw error;
    }
  },

  /**
   * Check if a user is attending a meeting
   * @param {string} userId - UUID of the user
   * @param {string} meetingId - UUID of the meeting
   * @returns {Promise<Object|null>} Attendee record or null
   */
  isUserAttending: async (userId, meetingId) => {
    try {
      const { data, error } = await supabase
        .from('meeting_attendees')
        .select('*')
        .eq('user_id', userId)
        .eq('meeting_id', meetingId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // User not attending
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error checking if user is attending:', error);
      throw error;
    }
  },

  /**
   * Get pending RSVPs for a user (meetings they haven't responded to)
   * @param {string} userId - UUID of the user
   * @returns {Promise<Array>} Array of pending meetings
   */
  getPendingRSVPs: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('meeting_attendees')
        .select(`
          *,
          meetings (
            id,
            meeting_date,
            start_time,
            status,
            location_name,
            location_address,
            restaurant_name
          )
        `)
        .eq('user_id', userId)
        .eq('rsvp_status', 'pending')
        .eq('attendee_type', 'physician') // Only physicians need to RSVP
        .gte('meetings.meeting_date', new Date().toISOString().split('T')[0])
        .order('meetings(meeting_date)', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting pending RSVPs:', error);
      throw error;
    }
  }
};
